import {
  Button,
  ButtonGroup,
  Center,
  Checkbox,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  useBoolean,
  useDisclosure,
} from "@chakra-ui/react";
import { Trans, t } from "@lingui/macro";
import {
  useAbraFlexiOiExportQuery,
  useAbraFlexiOutgoingProformaInvoicesExportQuery,
  useExpenseAbraFlexiExportQuery,
} from "@src/__generated__/urql-graphql";
import { InputDatePicker } from "@src/components/ui-kit";
import { CancelButton } from "@src/components/ui-kit/Button/CancelButton";
import { fieldToCheckboxProps } from "@src/utils/forms/inputHelpers";
import { date, required } from "@src/utils/forms/validators";
import { FieldState, FormState } from "formstate";
import { observer } from "mobx-react-lite";
import { forwardRef, useImperativeHandle, useState } from "react";

export interface AbraFlexiExportModalRef {
  open: () => void;
  close: () => void;
  isOpen: boolean;
}

class Form {
  from = new FieldState<Date>(new Date()).validators(required, date);
  to = new FieldState<Date>(new Date()).validators(required, date);
  outgoingInvoices = new FieldState<boolean>(true);
  proformaInvoices = new FieldState<boolean>(true);
  expenses = new FieldState<boolean>(true);

  form = new FormState([
    this.expenses,
    this.outgoingInvoices,
    this.proformaInvoices,
    this.from,
    this.to,
  ]);
}

export const AbraFlexiExportModal = observer<object, AbraFlexiExportModalRef>(
  forwardRef(function AbraFlexiExportModal(_, ref) {
    const { isOpen, onOpen, onClose } = useDisclosure({
      onClose: () => {
        setIsSubmitted.off();
      },
    });
    const [form] = useState(() => new Form());
    const [isSubmitted, setIsSubmitted] = useBoolean(false);

    useImperativeHandle(
      ref,
      () => ({
        open: onOpen,
        close: onClose,
        isOpen,
      }),
      [],
    );

    const queryOptions = {
      variables: {
        from: form.from.value,
        to: form.to.value,
      },
      pause: true,
    };

    const [resultExportOI, exportOI] = useAbraFlexiOiExportQuery(queryOptions);
    const [resultExportProformas, exportProformas] =
      useAbraFlexiOutgoingProformaInvoicesExportQuery(queryOptions);
    const [resultExportExpenses, exportExpenses] =
      useExpenseAbraFlexiExportQuery(queryOptions);

    const onExport = async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      const res = await form.from.validate();
      if (res.hasError) return;

      form.expenses.value && exportExpenses();
      form.outgoingInvoices.value && exportOI();
      form.proformaInvoices.value && exportProformas();

      setIsSubmitted.on();
    };

    return (
      <Modal isOpen={isOpen} onClose={onClose} trapFocus={false}>
        <ModalOverlay />
        <ModalContent>
          <form onSubmit={onExport}>
            <ModalHeader>
              <Trans>ABRA Flexi export</Trans>
            </ModalHeader>

            <ModalCloseButton />

            <ModalBody>
              {isSubmitted ? (
                <Center>
                  <Text>
                    <Trans>
                      Your export is being prepared. It may take a while. You
                      can close this pop-up and check your data in ABRA in 5
                      minutes.
                    </Trans>
                  </Text>
                </Center>
              ) : (
                <Stack>
                  <Checkbox {...fieldToCheckboxProps(form.outgoingInvoices)}>
                    <Trans>Outgoing invoices</Trans>
                  </Checkbox>
                  <Checkbox {...fieldToCheckboxProps(form.proformaInvoices)}>
                    <Trans>Proforma invoices</Trans>
                  </Checkbox>
                  <Checkbox {...fieldToCheckboxProps(form.expenses)}>
                    <Trans>Expenses</Trans>
                  </Checkbox>

                  <Stack pt="3" spacing="3">
                    <InputDatePicker
                      label={t`From`}
                      selected={form.from.value}
                      onChange={(val) => {
                        if (val) {
                          form.from.onChange(val.start);
                        }
                      }}
                      error={form.from.error}
                    />

                    <InputDatePicker
                      label={t`To`}
                      selected={form.to.value}
                      onChange={(val) => {
                        if (val) {
                          form.to.onChange(val.start);
                        }
                      }}
                      error={form.to.error}
                    />
                  </Stack>
                </Stack>
              )}
            </ModalBody>

            <ModalFooter>
              <ButtonGroup>
                <CancelButton onClick={onClose} />
                {!isSubmitted && (
                  <Button
                    isDisabled={
                      !form.expenses.value &&
                      !form.outgoingInvoices.value &&
                      !form.proformaInvoices.value
                    }
                    isLoading={
                      resultExportOI.fetching ||
                      resultExportProformas.fetching ||
                      resultExportExpenses.fetching
                    }
                    type="submit"
                  >
                    <Trans>Export</Trans>
                  </Button>
                )}
              </ButtonGroup>
            </ModalFooter>
          </form>
        </ModalContent>
      </Modal>
    );
  }),
);
